import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { EditorPageLayout } from '@/components/layout/EditorPageLayout'
import { FormationToolbar } from '@/components/formations/FormationToolbar'
import { FormationHeader } from '@/components/formations/FormationHeader'
import { FormationCanvas } from '@/components/canvas/FormationCanvas'
import { EditorCardsPanel } from '@/components/plays'
import { CanvasViewportProvider } from '@/contexts/CanvasViewportContext'
import { useSaveStatus } from '@/contexts/SaveStatusContext'
import { useFormationEditor } from '@/hooks/UseFormationEditor'
import { useConceptData } from '@/hooks/UseConceptData'
import { useFormationKeyboardShortcuts } from '@/hooks/UseFormationKeyboardShortcuts'
import { Loading } from '@/components/ui/Loading'
import './formation-editor-page.css'

export function FormationEditorContent() {
	const { playbookId, formationId: rawFormationId } = useParams<{ playbookId: string; formationId: string }>()
	const navigate = useNavigate()
	const { status: saveStatus, trackMutation } = useSaveStatus()
	const saveTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined)

	// Treat "new" as no formationId (create mode)
	const formationId = rawFormationId === 'new' ? undefined : rawFormationId
	const formationIdRef = useRef(formationId)
	formationIdRef.current = formationId

	const [showPanel, setShowPanel] = useState(true)

	const editor = useFormationEditor({
		formationId,
		playbookId: playbookId!,
	})

	const { formations } = useConceptData(playbookId!)

	// Keyboard shortcuts for formation tools
	useFormationKeyboardShortcuts({
		setDrawingState: (updates) => editor.setDrawingState({ ...editor.drawingState, ...updates }),
		setShowColorPicker: editor.setShowColorPicker,
	})

	// Debounced autosave
	useEffect(() => {
		if (!editor.isValid || !editor.name.trim() || editor.isLoading) return

		clearTimeout(saveTimerRef.current)
		saveTimerRef.current = setTimeout(() => {
			trackMutation(async () => {
				const savedId = await editor.save()
				// For new formations, silently update the URL without triggering a route change
				if (!formationIdRef.current && savedId) {
					formationIdRef.current = savedId
					window.history.replaceState(null, '', `/playbooks/${playbookId}/formation/${savedId}`)
				}
				return savedId
			}).catch(() => {})
		}, 2000)

		return () => clearTimeout(saveTimerRef.current)
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [editor.name, editor.mirroredName, editor.hashMode, editor.autoMirror, editor.changeCounter])

	const handleBack = useCallback(() => {
		// Flush pending save
		if (saveTimerRef.current && editor.isValid && editor.name.trim()) {
			clearTimeout(saveTimerRef.current)
			trackMutation(() => editor.save()).catch(() => {})
		}
		navigate(`/playbooks/${playbookId}?tab=formations`)
	}, [editor.isValid, editor.name, editor.save, playbookId, navigate, trackMutation])

	const handleOpenFormation = useCallback((id: string) => {
		// Flush pending save before navigating
		if (saveTimerRef.current && editor.isValid && editor.name.trim()) {
			clearTimeout(saveTimerRef.current)
			trackMutation(() => editor.save()).catch(() => {})
		}
		navigate(`/playbooks/${playbookId}/formation/${id}`)
	}, [editor.isValid, editor.name, editor.save, playbookId, navigate, trackMutation])

	const handleAddFormation = useCallback(() => {
		// Flush pending save before creating new
		if (saveTimerRef.current && editor.isValid && editor.name.trim()) {
			clearTimeout(saveTimerRef.current)
			trackMutation(() => editor.save()).catch(() => {})
		}
		navigate(`/playbooks/${playbookId}/formation/new`)
	}, [editor.isValid, editor.name, editor.save, playbookId, navigate, trackMutation])

	const formationItems = useMemo(() =>
		formations.map(f => ({
			id: f.id,
			name: f.name,
			thumbnail: f.thumbnail,
			linkedFormationId: f.linked_formation_id ?? undefined,
			labelObjects: f.labels?.map(l => ({ id: l.id, name: l.name, color: l.color })),
		})),
		[formations]
	)

	if (!playbookId) {
		return <Loading text="Missing playbook ID" />
	}

	if (editor.isLoading) {
		return <Loading fullscreen text="Loading formation..." />
	}

	return (
		<EditorPageLayout
			toolbar={
				<FormationToolbar
					drawingState={editor.drawingState}
					setDrawingState={editor.setDrawingState}
					hashMode={editor.hashMode}
					setHashMode={editor.handleHashModeChange}
					hashAlignment={editor.activeDialog === 'primary' ? editor.primaryHashAlignment : editor.mirrorHashAlignment}
					showColorPicker={editor.showColorPicker}
					setShowColorPicker={editor.setShowColorPicker}
					showHashDialog={editor.showHashDialog}
					setShowHashDialog={editor.setShowHashDialog}
					autoMirror={editor.autoMirror}
					onAutoMirrorToggle={editor.handleAutoMirrorToggle}
					linemenAtDefault={editor.activeDialog === 'primary' ? editor.primaryLinemenAtDefault : editor.mirrorLinemenAtDefault}
					onHashAlignmentChange={editor.handleHashAlignmentChange}
					saveStatus={saveStatus}
					showPanel={showPanel}
					onTogglePanel={() => setShowPanel(prev => !prev)}
				/>
			}
			header={
				<FormationHeader
					name={editor.name}
					onNameChange={editor.setName}
					mirroredName={editor.mirroredName}
					onMirroredNameChange={editor.setMirroredName}
					autoMirror={editor.autoMirror}
					onBackToPlaybook={handleBack}
				/>
			}
			canvasArea={
				<div className="formation-editor__canvas-area">
					{editor.autoMirror ? (
						<div className="formation-editor__dual-canvas">
							<div className="formation-editor__canvas-panel">
								<span className="formation-editor__canvas-label">Primary</span>
								<div ref={editor.canvasContainerRef} className="formation-editor__canvas-wrapper">
									<CanvasViewportProvider>
										<FormationCanvas
											hashAlignment={editor.primaryHashAlignment}
											initialPositions={editor.transformedPositions}
											onCanvasStateReady={editor.handleCanvasStateReady}
											externalPlayers={editor.externalPlayers}
											drawingState={editor.drawingState}
											onPlayersChange={editor.handlePlayersChange}
										/>
									</CanvasViewportProvider>
								</div>
							</div>
							<div className="formation-editor__canvas-panel">
								<span className="formation-editor__canvas-label">Mirrored</span>
								<div className="formation-editor__canvas-wrapper">
									<CanvasViewportProvider>
										<FormationCanvas
											hashAlignment={editor.mirrorHashAlignment}
											onCanvasStateReady={editor.handleMirroredCanvasStateReady}
											externalPlayers={editor.mirroredExternalPlayers}
											drawingState={editor.drawingState}
											onPlayersChange={editor.handleMirroredPlayersChange}
										/>
									</CanvasViewportProvider>
								</div>
							</div>
						</div>
					) : (
						<div ref={editor.canvasContainerRef} className="formation-editor__canvas-wrapper">
							<CanvasViewportProvider>
								<FormationCanvas
									hashAlignment={editor.primaryHashAlignment}
									initialPositions={editor.transformedPositions}
									onCanvasStateReady={editor.handleCanvasStateReady}
									externalPlayers={editor.externalPlayers}
									drawingState={editor.drawingState}
									onPlayersChange={editor.handlePlayersChange}
								/>
							</CanvasViewportProvider>
						</div>
					)}

					{editor.errorMessage && (
						<p className="formation-editor__error">{editor.errorMessage}</p>
					)}
				</div>
			}
			sidePanel={
				<EditorCardsPanel
					items={formationItems}
					cardType="formation"
					currentItemId={formationId}
					showPanel={showPanel}
					onOpen={handleOpenFormation}
					onAdd={handleAddFormation}
				/>
			}
		/>
	)
}
